import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Wedding } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field, TextInput } from '@/components/ui/Field';
import { ApiRequestError } from '@/lib/api';
import { useUpdateWedding } from './queries';

/** Editing the wedding itself: the same fields onboarding collects, in a drawer. */
export function EditWeddingDrawer({
  wedding,
  open,
  onClose,
}: {
  wedding: Wedding;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateWedding(wedding.wedId);

  const [title, setTitle] = useState(wedding.title);
  const [partnerOneName, setPartnerOne] = useState(wedding.partnerOneName ?? '');
  const [partnerTwoName, setPartnerTwo] = useState(wedding.partnerTwoName ?? '');
  const [date, setDate] = useState(wedding.date ?? '');
  const [city, setCity] = useState(wedding.city ?? '');
  const [error, setError] = useState<string>();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    try {
      await update.mutateAsync({
        title,
        partnerOneName,
        partnerTwoName,
        city,
        ...(date ? { date } : {}),
        // The copy we started from. The server refuses the write if the stored
        // wedding has moved on since.
        updatedAt: wedding.updatedAt,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'version_conflict'
          ? t('wedding.conflict')
          : t('common.somethingWrong'),
      );
    }
  }

  return (
    /*
     * The primary action is pinned, not inline at the end of the form.
     *
     * These drawers scroll, and a Save you have to go looking for is one people
     * stop trusting is there — it also sat among other buttons that look the
     * same, which is how you press the wrong one. `form` is the attribute that
     * lets a button outside the form submit it.
     */
    <Drawer
      open={open}
      title={t('wedding.editTitle')}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" form="edit-wedding" loading={update.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      }
    >
      <form id="edit-wedding" onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t('onboarding.weddingTitle')} hint={t('onboarding.weddingTitleHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('onboarding.partnerOne')}>
          {({ id }) => (
            <TextInput
              id={id}
              maxLength={200}
              value={partnerOneName}
              onChange={(e) => setPartnerOne(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('onboarding.partnerTwo')}>
          {({ id }) => (
            <TextInput
              id={id}
              maxLength={200}
              value={partnerTwoName}
              onChange={(e) => setPartnerTwo(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('onboarding.date')}>
          {({ id }) => (
            <TextInput id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          )}
        </Field>

        <Field label={t('onboarding.city')}>
          {({ id }) => (
            <TextInput
              id={id}
              maxLength={200}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-body text-status-declinedFg">
            {error}
          </p>
        )}
      </form>
    </Drawer>
  );
}
